import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Search, Edit2, Trash2, Eye, X, 
  CheckCircle, XCircle, AlertCircle, Clock,
  Calendar, MapPin, UserPlus, RefreshCw,
  ChevronLeft, ChevronRight, Filter, Users, Info
} from 'lucide-react';
import COEHeader from './COEHeader';
import GeofenceMapPicker from './GeofenceMapPicker';
import logoutManager from '../logout';
import api from '../api';

const ManageEvaluators = () => {
  const location = useLocation();
  const [user] = useState(location.state?.user || null);
  const [tenant] = useState(location.state?.tenant || null);
  const [orgInfo] = useState(location.state?.orgInfo || { name: '', logoUrl: '' });

  // State for evaluators list
  const [evaluators, setEvaluators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEvaluators, setTotalEvaluators] = useState(0);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEvaluator, setSelectedEvaluator] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    accessStartDateTime: '',
    accessEndDateTime: '',
    dailyHourLimit: 8,
    geofenceEnabled: false,
    geofenceLatitude: '',
    geofenceLongitude: '',
    geofenceRadius: 500
  });
  const [processing, setProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', message: '' });

  const updateGeofenceLocation = useCallback((lat, lng) => {
    setFormData((prev) => ({
      ...prev,
      geofenceLatitude: lat,
      geofenceLongitude: lng
    }));
  }, []);

  const updateGeofenceRadius = useCallback((nextRadius) => {
    setFormData((prev) => ({
      ...prev,
      geofenceRadius: nextRadius
    }));
  }, []);

  const tenantId = localStorage.getItem('tenantId') || tenant?.id || tenant?._id;

  const showStatus = (type, message) => {
    setStatusMessage({ type, message });
    setTimeout(() => setStatusMessage({ type: '', message: '' }), 5000);
  };

  // Fetch evaluators list
  const fetchEvaluators = useCallback(async () => {
    if (!tenantId) return;
    
    setLoading(true);
    try {
      const response = await api.get('/api/coe/evaluators/list', {
        params: {
          tenantId,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          page: currentPage,
          limit: 10
        },
        headers: { 'x-tenant-id': tenantId }
      });
      
      if (response.data.success) {
        setEvaluators(response.data.evaluators || []);
        setTotalPages(response.data.pagination?.pages || 1);
        setTotalEvaluators(response.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Error fetching evaluators:', error);
      showStatus('error', 'Failed to fetch evaluators list. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, statusFilter, currentPage]);

  useEffect(() => {
    fetchEvaluators();
  }, [fetchEvaluators]);

  // Create evaluator
  const handleCreateEvaluator = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phoneNumber) {
      showStatus('error', 'Please fill in all the required text boxes.');
      return;
    }

    if (!formData.accessStartDateTime || !formData.accessEndDateTime) {
      showStatus('error', 'Please select both a start date and an end date.');
      return;
    }

    setProcessing(true);
    
    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        tenantId,
        actorId: user?.authUserId || user?.userId,
        accessStartDateTime: formData.accessStartDateTime,
        accessEndDateTime: formData.accessEndDateTime,
        dailyHourLimit: formData.dailyHourLimit,
        geofenceEnabled: formData.geofenceEnabled,
        geofenceLatitude: formData.geofenceEnabled ? formData.geofenceLatitude : null,
        geofenceLongitude: formData.geofenceEnabled ? formData.geofenceLongitude : null,
        geofenceRadius: formData.geofenceEnabled ? formData.geofenceRadius : 500
      };
      
      const response = await api.post('/api/coe/evaluators/create', payload, {
        headers: { 'x-tenant-id': tenantId }
      });
      
      if (response.data.success) {
        showStatus('success', `Evaluator added successfully! The default log-in password is: ${response.data.data?.defaultPassword || 'Truyon@1234'}`);
        setShowCreateModal(false);
        resetForm();
        fetchEvaluators();
      }
    } catch (error) {
      console.error('Error creating evaluator:', error);
      showStatus('error', error.response?.data?.message || 'Could not add the evaluator. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Update evaluator access
  const handleUpdateAccess = async (e) => {
    e.preventDefault();
    setProcessing(true);
    
    try {
      const payload = {
        tenantId,
        actorId: user?.authUserId || user?.userId,
        accessStartDateTime: formData.accessStartDateTime,
        accessEndDateTime: formData.accessEndDateTime,
        dailyHourLimit: formData.dailyHourLimit,
        status: formData.status,
        geofenceEnabled: formData.geofenceEnabled,
        geofenceLatitude: formData.geofenceEnabled ? formData.geofenceLatitude : null,
        geofenceLongitude: formData.geofenceEnabled ? formData.geofenceLongitude : null,
        geofenceRadius: formData.geofenceEnabled ? formData.geofenceRadius : 500
      };
      
      const response = await api.put(`/api/coe/evaluators/${selectedEvaluator._id}/access`, payload, {
        headers: { 'x-tenant-id': tenantId }
      });
      
      if (response.data.success) {
        showStatus('success', 'Evaluator login access settings updated successfully.');
        setShowEditModal(false);
        resetForm();
        fetchEvaluators();
      }
    } catch (error) {
      console.error('Error updating evaluator:', error);
      showStatus('error', error.response?.data?.message || 'Could not update settings. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Delete evaluator
  const handleDeleteEvaluator = async () => {
    setProcessing(true);
    
    try {
      const response = await api.delete(`/api/coe/evaluators/${selectedEvaluator._id}`, {
        headers: { 'x-tenant-id': tenantId }
      });
      
      if (response.data.success) {
        showStatus('success', 'Evaluator removed successfully.');
        setShowDeleteModal(false);
        setSelectedEvaluator(null);
        fetchEvaluators();
      }
    } catch (error) {
      console.error('Error deleting evaluator:', error);
      showStatus('error', error.response?.data?.message || 'Could not remove the evaluator. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Toggle evaluator status
  const handleToggleStatus = async (evaluator, newStatus) => {
    try {
      const payload = {
        tenantId,
        actorId: user?.authUserId || user?.userId,
        status: newStatus
      };
      
      const response = await api.put(`/api/coe/evaluators/${evaluator._id}/access`, payload, {
        headers: { 'x-tenant-id': tenantId }
      });
      
      if (response.data.success) {
        showStatus('success', `Evaluator account is now ${newStatus === 'active' ? 'turned on' : 'turned off'}.`);
        fetchEvaluators();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      showStatus('error', error.response?.data?.message || 'Could not change the account status. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      accessStartDateTime: '',
      accessEndDateTime: '',
      dailyHourLimit: 8,
      geofenceEnabled: false,
      geofenceLatitude: '',
      geofenceLongitude: '',
      geofenceRadius: 500
    });
    setSelectedEvaluator(null);
  };

  const openEditModal = (evaluator) => {
    setSelectedEvaluator(evaluator);
    setFormData({
      firstName: evaluator.firstName || '',
      lastName: evaluator.lastName || '',
      email: evaluator.email || '',
      phoneNumber: evaluator.phoneNumber || '',
      accessStartDateTime: evaluator.temporaryAccess?.timeConstraint?.accessStartDateTime?.slice(0, 16) || '',
      accessEndDateTime: evaluator.temporaryAccess?.timeConstraint?.accessEndDateTime?.slice(0, 16) || '',
      dailyHourLimit: evaluator.temporaryAccess?.timeConstraint?.dailyHourLimit || 8,
      status: evaluator.temporaryAccess?.status || evaluator.status,
      geofenceEnabled: evaluator.temporaryAccess?.locationConstraint?.enabled || false,
      geofenceLatitude: evaluator.temporaryAccess?.locationConstraint?.latitude || '',
      geofenceLongitude: evaluator.temporaryAccess?.locationConstraint?.longitude || '',
      geofenceRadius: evaluator.temporaryAccess?.locationConstraint?.radiusInMeters || 500
    });
    setShowEditModal(true);
  };

  const openViewModal = (evaluator) => {
    setSelectedEvaluator(evaluator);
    setShowViewModal(true);
  };

  const openDeleteModal = (evaluator) => {
    setSelectedEvaluator(evaluator);
    setShowDeleteModal(true);
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 border border-emerald-200 text-emerald-800"><CheckCircle className="w-3 h-3 mr-1" />Active</span>;
      case 'expired':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 border border-amber-200 text-amber-800"><Clock className="w-3 h-3 mr-1" />Expired</span>;
      case 'revoked':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 border border-red-200 text-red-800"><XCircle className="w-3 h-3 mr-1" />Turned Off</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-50 border border-slate-200 text-slate-600">{status}</span>;
    }
  };

  // Restored: Search filter handler used inside the table and grid render workflows
  const filteredEvaluators = evaluators.filter(evaluator => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (evaluator.firstName?.toLowerCase() || '').includes(searchLower) ||
      (evaluator.lastName?.toLowerCase() || '').includes(searchLower) ||
      (evaluator.email?.toLowerCase() || '').includes(searchLower) ||
      (evaluator.phoneNumber || '').includes(searchTerm)
    );
  });

  return (
    <div className="min-h-screen w-full flex flex-col bg-slate-50 text-slate-900 antialiased font-sans">
      <COEHeader user={user} tenant={tenant} orgInfo={orgInfo} onLogout={() => logoutManager.logout({ redirectTo: '/' })} />

      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Main Header Banner Area */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 md:p-6 shadow-sm transition-all">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 text-blue-600 rounded-xl shadow-inner">
                <Users className="w-6 h-6 md:w-7 md:h-7" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">Manage Evaluators</h1>
                <p className="text-xs md:text-sm text-slate-500 font-medium mt-0.5">Add, edit, and view teachers or staff members assigned to grade exam papers.</p>
              </div>
            </div>
            <button
              onClick={openCreateModal}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs transition-colors shadow-sm"
            >
              <UserPlus className="w-4 h-4" /> Add Evaluator
            </button>
          </div>
        </div>

        {/* Dynamic Context Notification System */}
        {statusMessage.message && (
          <div className={`rounded-xl border p-4 shadow-sm animate-in fade-in slide-in-from-top-4 duration-200 ${
            statusMessage.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
            statusMessage.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
            <div className="flex items-start gap-3">
              {statusMessage.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
              <p className="text-sm font-semibold leading-5">{statusMessage.message}</p>
            </div>
          </div>
        )}

        {/* Filtering & Execution Tools Dashboard Grid */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search staff by name, email, or phone number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-xs bg-white font-medium focus:outline-none focus:border-indigo-500"
              />
            </div>
            <div className="flex items-center gap-2 justify-between md:justify-end">
              <div className="flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-slate-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white focus:outline-none text-slate-600 font-semibold"
                >
                  <option value="all">All Accounts</option>
                  <option value="active">Active Only</option>
                  <option value="expired">Expired Only</option>
                  <option value="revoked">Turned Off Only</option>
                </select>
              </div>
              <button
                onClick={() => fetchEvaluators()}
                className="p-2 text-slate-400 hover:text-indigo-600 border border-slate-200 bg-white hover:bg-slate-50 transition-colors rounded-xl shadow-sm"
                title="Refresh the page list"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Primary Evaluators Data Management Component Deck */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          
          {/* Desktop Matrix Render View Architecture */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Phone & ID</th>
                  <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Allowed Work Dates</th>
                  <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Daily Work Limit</th>
                  <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-16 text-center">
                      <div className="flex justify-center"><div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div></div>
                    </td>
                  </tr>
                ) : filteredEvaluators.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-16 text-center text-sm text-slate-400 font-semibold">No evaluators found matching your search.</td>
                  </tr>
                ) : (
                  filteredEvaluators.map((evaluator) => (
                    <tr key={evaluator._id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center font-bold text-xs text-indigo-600 shadow-inner flex-shrink-0">
                            {evaluator.firstName?.charAt(0)}{evaluator.lastName?.charAt(0)}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-slate-800 truncate">{evaluator.firstName} {evaluator.lastName}</p>
                            <p className="text-xs text-slate-400 truncate">{evaluator.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-xs font-bold text-slate-600">{evaluator.phoneNumber || '—'}</p>
                        <p className="text-[10px] font-mono font-medium text-slate-400 mt-0.5">ID: {evaluator._id?.slice(-8).toUpperCase()}</p>
                      </td>
                      <td className="px-6 py-4">
                        {evaluator.temporaryAccess?.timeConstraint ? (
                          <div className="space-y-1">
                            <p className="text-xs font-semibold text-slate-600 flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-slate-400" />
                              {new Date(evaluator.temporaryAccess.timeConstraint.accessStartDateTime).toLocaleDateString()} to {new Date(evaluator.temporaryAccess.timeConstraint.accessEndDateTime).toLocaleDateString()}
                            </p>
                            {evaluator.temporaryAccess.locationConstraint?.enabled && (
                              <span className="inline-flex items-center text-[10px] bg-indigo-50/50 text-indigo-700 px-1.5 py-0.5 rounded border border-indigo-100 font-bold">
                                <MapPin className="w-2.5 h-2.5 mr-0.5" /> Can only work from campus / specific location
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 font-medium">Dates not set</span>
                        )}
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(evaluator.temporaryAccess?.status || evaluator.status)}</td>
                      <td className="px-6 py-4"><span className="text-xs font-mono font-bold text-slate-600">{evaluator.temporaryAccess?.timeConstraint?.dailyHourLimit || 8} hours/day</span></td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => openViewModal(evaluator)} className="p-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 rounded-lg text-xs transition-colors" title="View details"><Eye className="w-4 h-4" /></button>
                          <button onClick={() => openEditModal(evaluator)} className="p-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 rounded-lg text-xs transition-colors" title="Edit allowed times and limits"><Edit2 className="w-4 h-4" /></button>
                          <button onClick={() => handleToggleStatus(evaluator, evaluator.temporaryAccess?.status === 'active' ? 'revoked' : 'active')} className="p-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 rounded-lg text-xs transition-colors" title={evaluator.temporaryAccess?.status === 'active' ? 'Turn Account Off' : 'Turn Account On'}><CheckCircle className="w-4 h-4" /></button>
                          <button onClick={() => openDeleteModal(evaluator)} className="p-1.5 border border-red-200 bg-red-50/20 hover:bg-red-50 text-red-600 rounded-lg text-xs transition-colors" title="Remove account permanently"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile UI/UX Flex Grid Deck Cards - Active Layout for Touch Screens */}
          <div className="block lg:hidden divide-y divide-slate-100 p-2">
            {loading ? (
              <div className="py-12 flex justify-center"><div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div></div>
            ) : filteredEvaluators.length === 0 ? (
              <p className="text-xs text-slate-400 font-semibold text-center py-12">No evaluators found matching your search.</p>
            ) : (
              filteredEvaluators.map((evaluator) => (
                <div key={evaluator._id} className="p-3 bg-white rounded-xl space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center font-bold text-xs text-indigo-600 flex-shrink-0">
                        {evaluator.firstName?.charAt(0)}{evaluator.lastName?.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs font-bold text-slate-800 truncate">{evaluator.firstName} {evaluator.lastName}</h4>
                        <p className="text-[11px] text-slate-400 truncate">{evaluator.email}</p>
                      </div>
                    </div>
                    {getStatusBadge(evaluator.temporaryAccess?.status || evaluator.status)}
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-2.5 border border-slate-100 text-[11px] text-slate-600 space-y-1">
                    <p className="truncate">Phone number: <strong className="text-slate-800">{evaluator.phoneNumber || '—'}</strong></p>
                    {evaluator.temporaryAccess?.timeConstraint && (
                      <p className="truncate flex items-center gap-1"><Calendar className="w-3 h-3 text-slate-400" /> Allowed: {new Date(evaluator.temporaryAccess.timeConstraint.accessStartDateTime).toLocaleDateString()} to {new Date(evaluator.temporaryAccess.timeConstraint.accessEndDateTime).toLocaleDateString()}</p>
                    )}
                    <p>Daily limit: <strong className="text-slate-800 font-mono">{evaluator.temporaryAccess?.timeConstraint?.dailyHourLimit || 8} hours/day</strong></p>
                  </div>

                  <div className="flex items-center justify-end gap-1.5 pt-1">
                    <button onClick={() => openViewModal(evaluator)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 bg-white">View Details</button>
                    <button onClick={() => openEditModal(evaluator)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 bg-white">Edit Access</button>
                    <button onClick={() => handleToggleStatus(evaluator, evaluator.temporaryAccess?.status === 'active' ? 'revoked' : 'active')} className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 bg-white">Toggle Status</button>
                    <button onClick={() => openDeleteModal(evaluator)} className="px-2 py-1.5 border border-red-200 rounded-lg text-xs font-bold text-red-600 bg-red-50/20">Remove</button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination Command Bar Footer Interface Block */}
          {totalPages > 1 && (
            <div className="px-5 py-3.5 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
              <p className="text-slate-500 font-medium">
                Showing <strong className="text-slate-700">{filteredEvaluators.length}</strong> of <strong className="text-slate-700">{totalEvaluators}</strong> total evaluators.
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-40 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="font-semibold text-slate-600">Page {currentPage} of {totalPages}</span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-40 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* OVERLAY SHEET MODAL OVERLAYS FLUIDLY SCALED FOR TOUCH WORKSPACES */}

      {/* Modal 1: Account Creation Console Form overlay */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-250">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col border border-slate-200 animate-in slide-in-from-bottom-8 duration-200">
            <div className="sticky top-0 bg-slate-50 border-b border-slate-100 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-50 rounded-xl text-indigo-600 shadow-inner"><UserPlus className="w-5 h-5" /></div>
                <div>
                  <h2 className="text-base font-bold text-slate-800">Add Temporary Evaluator Account</h2>
                  <p className="text-[11px] text-slate-400 font-medium mt-0.5">Fill out the fields below to create a new evaluator profile.</p>
                </div>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="p-1 rounded-full text-slate-400 hover:bg-slate-200"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleCreateEvaluator} className="p-5 space-y-5 overflow-y-auto flex-1 text-xs bg-white">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-slate-500 font-bold uppercase tracking-wide text-[10px]">First Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500"
                    placeholder="Enter first name"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-slate-500 font-bold uppercase tracking-wide text-[10px]">Last Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500"
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-slate-500 font-bold uppercase tracking-wide text-[10px]">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500"
                    placeholder="name@example.com"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-slate-500 font-bold uppercase tracking-wide text-[10px]">Phone Number *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    placeholder="Enter phone number"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-slate-500 font-bold uppercase tracking-wide text-[10px]">Access Allowed From (Date & Time) *</label>
                  <input
                    type="datetime-local"
                    required
                    value={formData.accessStartDateTime}
                    onChange={(e) => setFormData({ ...formData, accessStartDateTime: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-slate-500 font-bold uppercase tracking-wide text-[10px]">Access Allowed Until (Date & Time) *</label>
                  <input
                    type="datetime-local"
                    required
                    value={formData.accessEndDateTime}
                    onChange={(e) => setFormData({ ...formData, accessEndDateTime: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none font-mono"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-slate-500 font-bold uppercase tracking-wide text-[10px]">Maximum Work Hours Allowed Per Day</label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  value={formData.dailyHourLimit}
                  onChange={(e) => setFormData({ ...formData, dailyHourLimit: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none font-bold"
                />
                <p className="text-[10px] text-slate-400 font-medium pl-0.5">The total hours this user is permitted to work inside the software each day.</p>
              </div>

              <div className="border-t border-slate-100 pt-4 space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="geofenceEnabled"
                    checked={formData.geofenceEnabled}
                    onChange={(e) => setFormData({ ...formData, geofenceEnabled: e.target.checked })}
                    className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="geofenceEnabled" className="text-xs font-bold text-slate-700 uppercase tracking-wide select-none">
                    Restrict Work Access to a Specific Location (Geofence)
                  </label>
                </div>

                {formData.geofenceEnabled && (
                  <GeofenceMapPicker
                    enabled={formData.geofenceEnabled}
                    latitude={formData.geofenceLatitude}
                    longitude={formData.geofenceLongitude}
                    radius={formData.geofenceRadius}
                    onLocationChange={updateGeofenceLocation}
                    onRadiusChange={updateGeofenceRadius}
                    title="Choose the allowed evaluator location"
                    note="Click the map or drag the marker to place the geofence center. The circle shows the allowed work zone."
                  />
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 bg-slate-50 -mx-5 -mb-5 px-5 py-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-slate-300 rounded-xl bg-white font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm transition-colors flex items-center gap-2"
                >
                  {processing && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  Create Evaluator Profile
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 2: Edit Access Configuration Overlay Form component */}
      {showEditModal && selectedEvaluator && (
        <div className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-250">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col border border-slate-200 animate-in slide-in-from-bottom-8 duration-200">
            <div className="sticky top-0 bg-slate-50 border-b border-slate-100 px-5 py-4 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-800">Edit Evaluator Access Settings</h2>
              <button onClick={() => setShowEditModal(false)} className="p-1 rounded-full text-slate-400 hover:bg-slate-200"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleUpdateAccess} className="p-5 space-y-5 overflow-y-auto flex-1 text-xs bg-white">
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 font-bold flex items-center justify-center shadow-inner text-[10px] flex-shrink-0">
                  {selectedEvaluator.firstName?.charAt(0)}{selectedEvaluator.lastName?.charAt(0)}
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-800 truncate">{selectedEvaluator.firstName} {selectedEvaluator.lastName}</p>
                  <p className="text-[11px] font-mono text-slate-400 truncate">{selectedEvaluator.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-slate-400 font-bold uppercase tracking-wide text-[10px]">First Name</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none font-semibold"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-slate-400 font-bold uppercase tracking-wide text-[10px]">Last Name</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none font-semibold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-slate-400 font-bold uppercase tracking-wide text-[10px]">Email Address</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-slate-400 font-bold uppercase tracking-wide text-[10px]">Phone Number</label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-slate-400 font-bold uppercase tracking-wide text-[10px]">Access Allowed From (Date & Time)</label>
                  <input
                    type="datetime-local"
                    value={formData.accessStartDateTime}
                    onChange={(e) => setFormData({ ...formData, accessStartDateTime: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-slate-400 font-bold uppercase tracking-wide text-[10px]">Access Allowed Until (Date & Time)</label>
                  <input
                    type="datetime-local"
                    value={formData.accessEndDateTime}
                    onChange={(e) => setFormData({ ...formData, accessEndDateTime: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-slate-400 font-bold uppercase tracking-wide text-[10px]">Daily permitted hours limit</label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={formData.dailyHourLimit}
                    onChange={(e) => setFormData({ ...formData, dailyHourLimit: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none font-bold"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-slate-400 font-bold uppercase tracking-wide text-[10px]">Account Access Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none bg-white font-semibold text-slate-700"
                  >
                    <option value="active">Active (Account is on)</option>
                    <option value="expired">Expired (Time has run out)</option>
                    <option value="revoked">Turned Off (Access is blocked)</option>
                  </select>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4 space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="editGeofenceEnabled"
                    checked={formData.geofenceEnabled}
                    onChange={(e) => setFormData({ ...formData, geofenceEnabled: e.target.checked })}
                    className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="editGeofenceEnabled" className="text-xs font-bold text-slate-700 uppercase tracking-wide select-none">
                    Enable location restriction rules
                  </label>
                </div>

                {formData.geofenceEnabled && (
                  <GeofenceMapPicker
                    enabled={formData.geofenceEnabled}
                    latitude={formData.geofenceLatitude}
                    longitude={formData.geofenceLongitude}
                    radius={formData.geofenceRadius}
                    onLocationChange={updateGeofenceLocation}
                    onRadiusChange={updateGeofenceRadius}
                    title="Adjust the evaluator’s allowed zone"
                    note="The saved location appears on the map. Drag the marker or click anywhere else to move the center."
                  />
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 bg-slate-50 -mx-5 -mb-5 px-5 py-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-slate-300 rounded-xl bg-white font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm transition-colors flex items-center gap-2"
                >
                  {processing && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  Save Allocation Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 3: Account Profile Card overlay */}
      {showViewModal && selectedEvaluator && (
        <div className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-250">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-lg w-full border border-slate-200 overflow-hidden flex flex-col animate-in slide-in-from-bottom-6 duration-200">
            <div className="border-b border-slate-100 px-5 py-4 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-slate-400" />
                <h2 className="text-base font-bold text-slate-800">Evaluator Profile Details</h2>
              </div>
              <button onClick={() => setShowViewModal(false)} className="p-1 rounded-full text-slate-400 hover:bg-slate-200"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-4 text-xs">
              <div className="flex items-center gap-4 pb-4 border-b border-slate-100 bg-slate-50/30 p-3 rounded-xl border">
                <div className="w-12 h-12 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center font-bold text-sm text-indigo-600 flex-shrink-0 shadow-inner">
                  {selectedEvaluator.firstName?.charAt(0)}{selectedEvaluator.lastName?.charAt(0)}
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-slate-800 text-sm truncate">{selectedEvaluator.firstName} {selectedEvaluator.lastName}</h3>
                  <p className="text-[11px] font-mono text-slate-400 mt-0.5 truncate">{selectedEvaluator.email}</p>
                  <p className="text-[11px] text-slate-500 font-semibold mt-0.5">Phone: {selectedEvaluator.phoneNumber || 'Not provided'}</p>
                </div>
              </div>

              <div className="divide-y divide-slate-100 font-medium text-slate-700 bg-white shadow-inner border border-slate-100 rounded-xl px-3 py-1">
                <div className="flex justify-between py-2.5 items-center">
                  <span className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">Account Status</span>
                  {getStatusBadge(selectedEvaluator.temporaryAccess?.status || selectedEvaluator.status)}
                </div>
                <div className="flex justify-between py-2.5 items-center">
                  <span className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">Allowed Dates Period</span>
                  <span className="font-semibold text-slate-700">
                    {selectedEvaluator.temporaryAccess?.timeConstraint?.accessStartDateTime ? 
                      `${new Date(selectedEvaluator.temporaryAccess.timeConstraint.accessStartDateTime).toLocaleDateString()} to ${new Date(selectedEvaluator.temporaryAccess.timeConstraint.accessEndDateTime).toLocaleDateString()}` : 'No date restrictions configured'}
                  </span>
                </div>
                <div className="flex justify-between py-2.5 items-center">
                  <span className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">Hours Limit Per Day</span>
                  <span className="font-bold text-slate-700">{selectedEvaluator.temporaryAccess?.timeConstraint?.dailyHourLimit || 8} working hours/day</span>
                </div>
                <div className="flex justify-between py-2.5 items-center">
                  <span className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">Location Lock Settings</span>
                  <span className={`font-bold ${selectedEvaluator.temporaryAccess?.locationConstraint?.enabled ? 'text-indigo-600' : 'text-slate-400'}`}>
                    {selectedEvaluator.temporaryAccess?.locationConstraint?.enabled ? 'Locked to specific location' : 'Can work from anywhere'}
                  </span>
                </div>
                {selectedEvaluator.temporaryAccess?.locationConstraint?.enabled && (
                  <>
                    <div className="flex justify-between py-2.5 items-center pl-2">
                      <span className="text-slate-400 text-[10px]">Allowed Location Coordinates</span>
                      <span className="font-mono text-slate-600">{selectedEvaluator.temporaryAccess.locationConstraint.latitude}, {selectedEvaluator.temporaryAccess.locationConstraint.longitude}</span>
                    </div>
                    <div className="flex justify-between py-2.5 items-center pl-2">
                      <span className="text-slate-400 text-[10px]">Allowed Radius Area Range</span>
                      <span className="text-slate-600 font-bold">{selectedEvaluator.temporaryAccess.locationConstraint.radiusInMeters} meters around center</span>
                    </div>
                  </>
                )}
              </div>

              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setShowViewModal(false)}
                  className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-colors text-center"
                >
                  Close Profile Window
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal 4: Account Delete Confirmation Alert dialog overlay */}
      {showDeleteModal && selectedEvaluator && (
        <div className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-150">
            <div className="bg-red-50/50 px-5 py-4 border-b border-red-100 flex items-center gap-3">
              <div className="p-2 bg-red-100 text-red-600 rounded-full shadow-sm"><Trash2 className="w-5 h-5" /></div>
              <h2 className="text-sm font-bold text-red-900">Remove Evaluator Account Permanently</h2>
            </div>

            <div className="p-5 space-y-4 text-xs">
              <p className="text-slate-600 leading-relaxed font-semibold">
                Are you sure you want to permanently delete the profile for <strong className="text-slate-900 font-bold">{selectedEvaluator.firstName} {selectedEvaluator.lastName}</strong>?
              </p>
              <p className="bg-red-50 text-red-700 p-3 rounded-xl border border-red-100 font-medium leading-normal">
                Warning: Confirming this action deletes the evaluator from the system immediately. Their login credentials, access permissions, and account data will be erased and cannot be recovered.
              </p>

              <div className="flex gap-2 justify-end pt-1">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 border border-slate-300 bg-white text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteEvaluator}
                  disabled={processing}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-sm transition-colors flex items-center justify-center gap-2"
                >
                  {processing && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  Yes, Remove Profile Permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageEvaluators;